/**
 * Utility functions to format text for natural Text-to-Speech pronunciation in Italian
 */

/**
 * Format text for optimal Text-to-Speech pronunciation
 * Converts abbreviations, symbols, and numbers to natural Italian speech
 */
export function formatTextForTTS(text: string): string {
  let formattedText = text;
  
  // Convert metal purities to natural speech
  formattedText = formattedText.replace(/24KT|24Kt/g, '24 carati');
  formattedText = formattedText.replace(/22KT|22Kt/g, '22 carati');
  formattedText = formattedText.replace(/20KT|20Kt/g, '20 carati');
  formattedText = formattedText.replace(/18KT|18Kt/g, '18 carati');
  formattedText = formattedText.replace(/14KT|14Kt/g, '14 carati');
  formattedText = formattedText.replace(/9KT|9Kt/g, '9 carati');
  
  // Generic rules for any other KT cases (after specific ones)
  formattedText = formattedText.replace(/\b(\d+)KT\b/g, '$1 carati');
  formattedText = formattedText.replace(/\b(\d+)Kt\b/g, '$1 carati');
  formattedText = formattedText.replace(/\bKT\b/g, 'carati');
  formattedText = formattedText.replace(/\bKt\b/g, 'carati');
  formattedText = formattedText.replace(/\bkt\b/g, 'carati');
  
  // Convert silver purities to natural speech
  formattedText = formattedText.replace(/999‰|PURO/g, 'puro');
  formattedText = formattedText.replace(/925‰/g, '925 millesimi');
  formattedText = formattedText.replace(/800‰/g, '800 millesimi');
  
  // Convert price units to natural speech for TTS
  formattedText = formattedText.replace(/€\/g|euro\/grammo|grammo\/euro/g, 'euro al grammo');
  formattedText = formattedText.replace(/€\/kg|euro\/chilogrammo|chilogrammo\/euro/g, 'euro al chilogrammo');
  
  // Convert units to natural speech (only when not part of price announcements)
  formattedText = formattedText.replace(/\bkg\b/g, 'chilo');
  formattedText = formattedText.replace(/\bg\b/g, 'grammo');
  
  // Convert symbols to natural speech
  formattedText = formattedText.replace(/‰/g, 'millesimi');
  formattedText = formattedText.replace(/€/g, 'euro');
  formattedText = formattedText.replace(/%/g, 'percento');
  
  // Format currency amounts for natural pronunciation
  formattedText = formatCurrencyForTTS(formattedText);
  
  // Clean up extra spaces
  formattedText = formattedText.replace(/\s+/g, ' ').trim();
  
  return formattedText;
}

/**
 * Format text specifically for metal price announcements
 * Avoids duplicate conversions that cause repetitions
 */
export function formatMetalPriceTextForTTS(text: string): string {
  let formattedText = text;
  
  // Convert metal purities to natural speech
  formattedText = formattedText.replace(/24KT|24Kt/g, '24 carati');
  formattedText = formattedText.replace(/22KT|22Kt/g, '22 carati');
  formattedText = formattedText.replace(/20KT|20Kt/g, '20 carati');
  formattedText = formattedText.replace(/18KT|18Kt/g, '18 carati');
  formattedText = formattedText.replace(/14KT|14Kt/g, '14 carati');
  formattedText = formattedText.replace(/9KT|9Kt/g, '9 carati');
  
  // Generic rules for any other KT cases (after specific ones)
  formattedText = formattedText.replace(/\b(\d+)KT\b/g, '$1 carati');
  formattedText = formattedText.replace(/\b(\d+)Kt\b/g, '$1 carati');
  formattedText = formattedText.replace(/\bKT\b/g, 'carati');
  formattedText = formattedText.replace(/\bKt\b/g, 'carati');
  formattedText = formattedText.replace(/\bkt\b/g, 'carati');
  
  // Convert silver purities to natural speech
  formattedText = formattedText.replace(/999‰|PURO/g, 'puro');
  formattedText = formattedText.replace(/925‰/g, '925 millesimi');
  formattedText = formattedText.replace(/800‰/g, '800 millesimi');

  // Convert only symbols, not units (handled manually in price announcements)
  formattedText = formattedText.replace(/‰/g, 'millesimi');
  formattedText = formattedText.replace(/%/g, 'percento');
  
  // Clean up extra spaces
  formattedText = formattedText.replace(/\s+/g, ' ').trim();
  
  return formattedText;
}

/**
 * Format currency amounts for natural TTS pronunciation
 * Converts "65.78" to "65 euro e 78 centesimi"
 */
function formatCurrencyForTTS(text: string): string {
  // Pattern to handle both dot and comma decimal formats
  const currencyPattern = /(\d{1,3}(?:[.,]\d{3})*)[,.](\d{1,2})\s*(?:euro|€)/g;
  
  return text.replace(currencyPattern, (match, euros, cents) => {
    // Remove dots/commas used as thousand separators, keep only the last part
    const cleanEuros = euros.replace(/[.,]/g, '');
    const eurosNum = parseInt(cleanEuros);
    
    const eurosPart = eurosNum === 1 ? '1 euro' : `${eurosNum} euro`;
    
    // Handle cents - pad with zero if single digit
    const centsNum = parseInt(cents.padEnd(2, '0'));
    let centsPart = '';
    
    if (centsNum > 0) {
      centsPart = centsNum === 1 ? ' e 1 centesimo' : ` e ${centsNum} centesimi`;
    }
    
    return eurosPart + centsPart;
  });
}

/**
 * Format date and time for natural TTS pronunciation in Italian
 */
function formatDateTimeForTTS(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  
  // Check if it's today
  const isToday = date.toDateString() === now.toDateString();
  
  // Check if it's yesterday
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();
  
  // Format time
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const timeStr = `${hours === 1 ? 'una' : hours} e ${minutes === 1 ? 'un minuto' : `${minutes} ${minutes === 0 ? '' : 'minuti'}`}`.replace(' e 0 ', ' ');
  
  if (isToday) {
    return `aggiornato oggi alle ${timeStr}`;
  } else if (isYesterday) {
    return `aggiornato ieri alle ${timeStr}`;
  } else {
    // Format full date
    const day = date.getDate();
    const month = date.toLocaleDateString('it-IT', { month: 'long' });
    return `aggiornato il ${day} ${month} alle ${timeStr}`;
  }
}

/**
 * Format metal price announcement for TTS
 */
export function formatMetalPriceForTTS(
  metal: 'gold' | 'silver',
  purity: string,
  price: string,
  unit: string = 'grammo',
  timestamp?: string
): string {
  const metalName = metal === 'gold' ? 'oro' : 'argento';
  const article = metal === 'gold' ? "dell'" : "dell'";
  
  // Clean price for better processing and format with comma
  const cleanPrice = price.replace(/€\s*/g, '').trim();
  const formattedPurity = formatPurityForTTS(purity, metal);
  const formattedUnit = unit === 'kg' ? 'chilo' : 'grammo';
  
  // Build the base text with proper Italian formatting
  let baseText = `Il prezzo attuale ${article}${metalName} ${formattedPurity} è ${cleanPrice} euro`;
  
  // Add timestamp information if provided
  if (timestamp) {
    const dateTimeInfo = formatDateTimeForTTS(timestamp);
    baseText += `, ${dateTimeInfo}`;
  }
  
  baseText += '.';
  
  // Use specialized formatter to avoid repetitions
  return formatMetalPriceTextForTTS(baseText);
}

/**
 * Format purity for TTS pronunciation
 */
function formatPurityForTTS(purity: string, metal: 'gold' | 'silver'): string {
  if (metal === 'gold') {
    switch (purity) {
      case '24KT': return '24 carati';
      case '22KT': return '22 carati';
      case '20KT': return '20 carati';
      case '18KT': return '18 carati';
      case '14KT': return '14 carati';
      case '9KT': return '9 carati';
      default: 
        // Generic handling for any KT format
        return purity.replace(/\b(\d+)KT\b/g, '$1 carati')
                    .replace(/\b(\d+)Kt\b/g, '$1 carati')
                    .replace(/\bKT\b/g, 'carati')
                    .replace(/\bKt\b/g, 'carati')
                    .replace(/\bkt\b/g, 'carati');
    }
  } else {
    switch (purity) {
      case 'PURO': return 'puro';
      case '925': return '925 millesimi';
      case '800': return '800 millesimi';
      default: return purity.replace('‰', ' millesimi');
    }
  }
}

/**
 * Format percentage change for TTS
 */
export function formatPercentageChangeForTTS(change: number, percentage: number): string {
  const direction = change >= 0 ? 'aumentato' : 'diminuito';
  const changeText = Math.abs(change).toFixed(2);
  const percentageText = Math.abs(percentage).toFixed(2);
  
  const baseText = `Il prezzo è ${direction} di ${changeText} euro, pari al ${percentageText} percento.`;
  
  return formatTextForTTS(baseText);
}

/**
 * Format valuation result for TTS pronunciation
 * Generates: "Il valore stimato del tuo oro 18 carati, peso 10 grammi, è 141 euro e 54 centesimi."
 */
export function formatValuationForTTS(
  metal: 'gold' | 'silver',
  purity: string,
  totalValue: string,
  weight: string,
  unit: string
): string {
  const metalName = metal === 'gold' ? 'oro' : 'argento';
  const formattedPurity = formatPurityForTTS(purity, metal);
  
  const unitMap: Record<string, string> = { g: 'grammi', kg: 'chili', oz: 'once' };
  const formattedUnit = unitMap[unit] || unit;
  
  // Clean price: remove € and whitespace
  const cleanPrice = totalValue.replace(/€\s*/g, '').trim();
  
  const baseText = `Il valore stimato del tuo ${metalName} ${formattedPurity}, peso ${weight} ${formattedUnit}, è ${cleanPrice} euro.`;
  
  return formatMetalPriceTextForTTS(baseText);
}